import { useRef, type KeyboardEvent, type MouseEvent, type ReactElement, type ReactNode } from "react";
import { logical } from "./metrics.ts";
import { maskPath, smallDigits } from "./small-digits.ts";

/**
 * Top tabs (the design's `Tabs`, `components/navigation/Tabs.jsx`, a visual reference; ADR-068's amendment "scope
 * tabs, and Help's icon"): a row of tabs in the pixel face, each 14 logical px tall with the frame's edge on three
 * sides and the chosen one 16 tall on the surface in brass with a brass rule along its top, over a foot ruled in the
 * frame's edge, and a count badge after a tab's label. A `tablist` of `tab`s with a roving tab index: the chosen tab
 * is the one Tab stop; while a tab has focus Left and Right choose the tab before and after it, wrapping, and Home and
 * End the first and the last, and those four keys are the only keys the row takes (ADR-061's amendment of
 * 2026-10-03). A key it answers is prevented, so the input router and Phaser, which both ignore a prevented key,
 * never move the character with it. A pointer press chooses a tab without taking focus, so the movement keys keep
 * moving the character after a click; while a tab of the row already has focus, the clicked tab takes it, so Enter
 * never chooses the tab the click left.
 */

/**
 * The tab's height, the chosen tab's, the label's padding each side, the gap between tabs and between a label and its
 * badge, in logical px, from the design's `.ot-tab`.
 */
export const TAB_METRICS = { height: 14, chosenHeight: 16, paddingX: 5, gap: 1, badgeGap: 3 } as const;

/**
 * The badge's height, the space each side of its digits inside its edge, and its digits' top from its top edge, in
 * logical px: the design's 10 tall badge with the small digits in place of the pixel face, whose measured line does
 * not fit a 10 px box.
 */
export const TAB_BADGE = { height: 10, paddingX: 2, digitsTop: 2 } as const;

/**
 * One tab: its id, what it shows, its accessible name (which says its count, if it has one), its badge's text or null
 * for none, and attributes for the caller to find it by.
 */
export interface TabEntry<T extends string> {
  readonly id: T;
  readonly label: ReactNode;
  readonly name: string;
  readonly badge?: string | null;
  readonly data?: Readonly<Record<`data-${string}`, string>>;
}

/**
 * The tab a key moves to from a focused tab: Left and Right the one before and after, wrapping, Home and End the
 * first and the last.
 *
 * @param ids The tabs' ids, in order.
 * @param from The focused tab's id; one not among them counts as the first.
 * @param key The key, as `KeyboardEvent.key` names it.
 * @returns The id, or null for a key the row does not take or a row with no tab.
 * @example
 * tabForKey(["all", "local", "whisper"], "all", "ArrowLeft");
 * // "whisper"
 */
export function tabForKey<T extends string>(ids: readonly T[], from: T, key: string): T | null {
  const at = Math.max(0, ids.indexOf(from));
  switch (key) {
    case "ArrowLeft":
      return ids[(at - 1 + ids.length) % ids.length] ?? null;
    case "ArrowRight":
      return ids[(at + 1) % ids.length] ?? null;
    case "Home":
      return ids[0] ?? null;
    case "End":
      return ids.at(-1) ?? null;
    default:
      return null;
  }
}

/**
 * A tab's badge: the count in the small digits, light on the danger fill inside the frame's edge, hidden from
 * assistive technology, since the tab's name says the count.
 *
 * @param props The `text`, digits and `+` only.
 * @returns The badge.
 */
export function TabBadge({ text }: { readonly text: string }): ReactElement {
  const digits = smallDigits(text);
  const inner = digits.width - 2;
  const width = Math.max(TAB_BADGE.height, inner + 2 * TAB_BADGE.paddingX + 2);
  return (
    <span className="ot-tab__badge" data-kit="tab-badge" aria-hidden="true" style={{ width: logical(width), height: logical(TAB_BADGE.height) }}>
      <svg viewBox={`1 1 ${inner} ${digits.height - 2}`} shapeRendering="crispEdges" style={{ left: logical(Math.floor((width - 2 - inner) / 2)), top: logical(TAB_BADGE.digitsTop - 1), width: logical(inner), height: logical(digits.height - 2) }}>
        <path d={maskPath(digits.glyph)} />
      </svg>
    </span>
  );
}

/**
 * Keeps focus where it was when a tab is pressed with the pointer; the click still chooses the tab.
 *
 * @param event The press.
 */
function keepFocus(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * A row of tabs.
 *
 * @param props The row's accessible `label`, its `tabs`, the chosen `value`, `onChange` with the id chosen,
 *   `tabId` giving each tab's element id, and the id of the panel the tabs control (`panelId`).
 * @returns The tab list.
 */
export function Tabs<T extends string>({ label, tabs, value, onChange, tabId, panelId }: { readonly label: string; readonly tabs: readonly TabEntry<T>[]; readonly value: T; readonly onChange: (id: T) => void; readonly tabId: (id: T) => string; readonly panelId: string }): ReactElement {
  const buttons = useRef(new Map<T, HTMLButtonElement>());
  const ids = tabs.map((tab) => tab.id);

  /**
   * Moves the choice with Left, Right, Home and End, and focuses the tab chosen.
   *
   * @param event The key press on a tab.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const from = ids.find((id) => buttons.current.get(id) === event.target);
    if (from === undefined || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    const to = tabForKey(ids, from, event.key);
    if (to === null) {
      return;
    }
    event.preventDefault();
    onChange(to);
    buttons.current.get(to)?.focus();
  };

  return (
    <div role="tablist" aria-label={label} className="ot-tabs" data-kit="tabs" style={{ gap: logical(TAB_METRICS.gap) }} onKeyDown={onKeyDown}>
      {tabs.map((tab) => {
        const chosen = tab.id === value;
        return (
          <button
            key={tab.id}
            ref={(element) => {
              if (element === null) {
                buttons.current.delete(tab.id);
              } else {
                buttons.current.set(tab.id, element);
              }
            }}
            id={tabId(tab.id)}
            type="button"
            role="tab"
            aria-selected={chosen}
            aria-controls={panelId}
            aria-label={tab.name}
            tabIndex={chosen ? 0 : -1}
            className="ot-tab"
            data-kit="tab"
            onMouseDown={keepFocus}
            onClick={() => {
              onChange(tab.id);
              if ([...buttons.current.values()].some((button) => button === document.activeElement)) {
                buttons.current.get(tab.id)?.focus();
              }
            }}
            style={{ height: logical(chosen ? TAB_METRICS.chosenHeight : TAB_METRICS.height), paddingLeft: logical(TAB_METRICS.paddingX), paddingRight: logical(TAB_METRICS.paddingX), gap: logical(TAB_METRICS.badgeGap) }}
            {...tab.data}
          >
            {tab.label}
            {tab.badge === undefined || tab.badge === null ? null : <TabBadge text={tab.badge} />}
          </button>
        );
      })}
    </div>
  );
}
